import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { MapPin, ChevronDown, Crown, Check } from "lucide-react";
import { toast } from "sonner";

const STORAGE_KEY = "bbs_location";
export const getSelectedLocation = () => localStorage.getItem(STORAGE_KEY) || "";

export function LocationSelector({ className = "" }) {
  const [areas, setAreas] = useState([]);
  const [current, setCurrent] = useState(getSelectedLocation());
  const [openList, setOpenList] = useState(false);
  const [unserviceable, setUnserviceable] = useState(null);
  const [notify, setNotify] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", email: "" });
  const [sending, setSending] = useState(false);

  useEffect(() => { api.get("/delivery-areas").then((r) => setAreas(r.data)).catch(() => {}); }, []);

  const choose = (area) => {
    setOpenList(false);
    if (area.active) {
      localStorage.setItem(STORAGE_KEY, area.city);
      setCurrent(area.city);
      window.dispatchEvent(new CustomEvent("bbs-location", { detail: area.city }));
      toast.success(`Delivering to ${area.city}`);
    } else {
      setUnserviceable(area);
    }
  };

  const submitNotify = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) { toast.error("Please enter your name and mobile"); return; }
    setSending(true);
    try {
      await api.post("/leads", {
        name: form.name, phone: form.phone, email: form.email || null,
        location: unserviceable?.city, requirement: "Location Interest",
        source: "Website Location Selector",
      });
      toast.success("Thanks! We'll notify you when we launch in your area.");
      setNotify(false); setUnserviceable(null); setForm({ name: "", phone: "", email: "" });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSending(false); }
  };

  return (
    <div className={`relative ${className}`} data-testid="location-selector">
      <button onClick={() => setOpenList((o) => !o)} data-testid="location-selector-btn"
        className="flex items-center gap-1.5 text-xs text-gold/90 hover:text-gold transition-colors">
        <MapPin size={13} /> Delivering to:
        <span className="text-cream/90 font-medium underline underline-offset-2">{current || "Select Location"}</span>
        <ChevronDown size={13} className={`transition-transform ${openList ? "rotate-180" : ""}`} />
      </button>

      {openList && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpenList(false)} />
          <div className="absolute z-50 mt-2 left-0 w-56 bg-white rounded-xl shadow-2xl border border-black/5 overflow-hidden" data-testid="location-dropdown">
            {areas.map((a) => (
              <button key={a.id} onClick={() => choose(a)} data-testid={`location-option-${a.city.toLowerCase().replace(/\s+/g, "-")}`}
                className="w-full flex items-center justify-between px-4 py-3 text-sm text-navy hover:bg-beige transition-colors border-b border-black/5 last:border-0">
                <span className="flex items-center gap-2">{a.city}{current === a.city && <Check size={14} className="text-gold" />}</span>
                <span className={`text-[10px] px-2 py-0.5 rounded-full ${a.active ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}`}>{a.active ? "Serviceable" : "Coming soon"}</span>
              </button>
            ))}
          </div>
        </>
      )}

      <Dialog open={!!unserviceable} onOpenChange={(o) => { if (!o) { setUnserviceable(null); setNotify(false); } }}>
        <DialogContent className="max-w-md" data-testid="unserviceable-modal">
          <DialogHeader>
            <div className="mx-auto w-12 h-12 rounded-full bg-navy text-gold flex items-center justify-center mb-2"><Crown size={22} /></div>
            <DialogTitle className="font-serif text-2xl text-center text-navy">Currently Unserviceable</DialogTitle>
            <DialogDescription className="text-sm text-muted-foreground text-center">We're sorry! Begum's Biryani Secrets is currently servicing <span className="font-semibold text-navy">Gurgaon</span> only. We're working towards expanding our delivery service to <span className="font-semibold text-navy">{unserviceable?.city}</span> soon.</DialogDescription>
          </DialogHeader>

          {!notify ? (
            <div className="flex flex-col sm:flex-row gap-2 mt-4">
              <button onClick={() => setUnserviceable(null)} data-testid="unserviceable-ok-btn" className="flex-1 border border-navy/20 text-navy font-semibold py-2.5 rounded-full hover:border-gold transition-colors">OK, GOT IT</button>
              <button onClick={() => setNotify(true)} data-testid="notify-me-btn" className="flex-1 bg-gold text-navy font-semibold py-2.5 rounded-full hover:bg-gold-light transition-colors">NOTIFY ME WHEN AVAILABLE</button>
            </div>
          ) : (
            <form onSubmit={submitNotify} className="space-y-3 mt-4" data-testid="notify-form">
              <input placeholder="Your Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="notify-name" className="w-full border border-black/15 rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-gold" />
              <input placeholder="Mobile Number" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="notify-phone" className="w-full border border-black/15 rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-gold" />
              <input placeholder="Email (optional)" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="notify-email" className="w-full border border-black/15 rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-gold" />
              <button type="submit" disabled={sending} data-testid="notify-submit" className="w-full bg-navy text-cream font-semibold py-2.5 rounded-full hover:bg-navy-soft transition-colors disabled:opacity-60">{sending ? "Submitting..." : "Notify Me"}</button>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
